// The live operations page's client (module 14; module 10 U103 to U106). Its own fetch helper and
// its own types, as Usage and Engineering have: nothing else in the portal reads these shapes.
// They mirror apps/api/src/operations/view.ts and routes/operations.ts.
import { viewFetch as fetch } from '../view-as.js';
import { ApiError, Unauthenticated } from '../api.js';

export type SourceState = 'arriving' | 'quiet' | 'waiting' | 'no_route';
export type ClockState = 'waiting' | 'passed' | 'met' | 'none';

export interface SourceReading {
  key: string; name: string; feeds: 'open' | 'machines' | 'company' | 'sport';
  arrives: string; state: SourceState; said: string | null; lastAt: string | null; lagHours: number | null;
}
export interface Clock {
  key: 'chase' | 'client_update' | 'escalate' | 'contract';
  words: string; state: ClockState; dot: boolean; at: string | null; pastMinutes: number | null; from: string | null;
}
export interface IncidentCard {
  id: string;
  severityWords: string; severityProvisional: boolean; loggedByFloor: boolean;
  section: string; supplier: string | null; issueSource: string | null; operators: string[];
  runMinutes: number; headline: string; gist: string;
  client: { text: string | null; readFrom: string | null };
  clocks: Clock[];
  told: string | null;
  readFrom: Array<{ words: string; at: string }>;
  evidence: Array<{ id: string; words: string; said: string | null; quote: string | null; at: string; source: string; joinBy: string }>;
  stillOpenBecause: string;
  breached: boolean;
}
export interface Plate {
  key: 'open' | 'past' | 'today' | 'channels' | 'waiting' | 'closed' | 'incidents' | 'resolve' | 'unresolved' | 'several';
  figure: string | null; label: string; under: string | null; warn: boolean; short: string | null;
}
/** One month drawn twice: incidents, and the hours of sport booked against them (frame 5). */
export interface MonthBar {
  month: string; label: string;
  incidents: number; incidentsHeight: number;
  /** Null is never zero here: it is a month the booking feed never recorded, and `recorded` says so. */
  hours: number | null; hoursHeight: number | null;
  fixtures: number; recorded: boolean;
}
/** The company's own measures. It carries no uptime, deliberately: see `uptimeSays`. */
export interface CompanyMeasures {
  available: boolean;
  says: string | null;
  copiedAt: string | null;
  year: number | null;
  plates: Plate[];
  behind: string | null;
  uptimeSays: string | null;
  months: MonthBar[] | null;
  monthsSays: string | null;
  sources: { label: string; n: number; share: string }[] | null;
  total: string | null;
}
export interface OperationsView {
  readAt: string;
  day: { date: string; startsAt: string; endsAt: string; hour: number };
  plates: Plate[];
  leadSays: string | null;
  leadPlates: Plate[] | null;
  lateness: { machines: string | null; floor: string | null };
  open: IncidentCard[];
  openSays: string | null;
  sportSays: string | null;
  contractsWaiting: string;
  read: SourceReading[];
  cannotSee: SourceReading[];
  company: CompanyMeasures;
  store: { tenantId: string; name: string };
}

async function json<T>(res: Response): Promise<T> {
  if (res.status === 401) throw new Unauthenticated();
  if (!res.ok) {
    const said = await res.json()
      .then((b: unknown) => (typeof b === 'object' && b !== null && typeof (b as { error?: unknown }).error === 'string' ? (b as { error: string }).error : null))
      .catch(() => null);
    throw new ApiError(res.status, said ?? `${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
}

export const operations = {
  read: async (): Promise<OperationsView> => json<OperationsView>(await fetch('/api/operations', { credentials: 'same-origin' })),
  access: async (): Promise<boolean> => {
    const res = await fetch('/api/operations/access', { credentials: 'same-origin' });
    if (!res.ok) return false;
    return ((await res.json()) as { allowed?: unknown }).allowed === true;
  },
};
